import React, { createContext, useContext, useState, useEffect, Suspense, lazy } from 'react';
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "@/components/ui/sonner";
import { HelmetProvider } from 'react-helmet-async';
import ErrorBoundary from "./components/ErrorBoundary";

// Eagerly loaded pages (critical path)
import HomePage from "./pages/HomePage";
import AuthPage from "./pages/AuthPage";
import AuthCallback from "./pages/AuthCallback";
import { LeaderboardPage } from "./pages/LeaderboardPage";

// Lazy loaded pages
const DirectoryPage = lazy(() => import("./pages/DirectoryPage"));
const ProfilePage = lazy(() => import("./pages/ProfilePage"));
const PricingPage = lazy(() => import("./pages/PricingPage"));
const ChatPage = lazy(() => import("./pages/ChatPage"));
const DashboardPage = lazy(() => import("./pages/DashboardPage"));
const UserDashboardPage = lazy(() => import("./pages/UserDashboardPage"));
const BlogDetailPage = lazy(() => import("./pages/BlogDetailPage"));
const SettingsPage = lazy(() => import("./pages/SettingsPage"));
const SubscriptionSuccessPage = lazy(() => import("./pages/SubscriptionSuccessPage"));
const AdvertisePage = lazy(() => import("./pages/AdvertisePage"));
const FeaturesPage = lazy(() => import("./pages/FeaturesPage"));
const CommunityPage = lazy(() => import("./pages/CommunityPage"));
const InboxPage = lazy(() => import("./pages/InboxPage"));
const BlogPage = lazy(() => import("./pages/BlogPage"));
const AdminPage = lazy(() => import("./pages/AdminPage"));
const SwapTrackerPage = lazy(() => import("./pages/SwapTrackerPage"));
const ComparisonPage = lazy(() => import("./pages/ComparisonPage"));
const ShareCardPage = lazy(() => import("./pages/ShareCardPage"));
const TestimonialsPage = lazy(() => import("./pages/TestimonialsPage"));
const EmbedWidgetPage = lazy(() => import("./pages/EmbedWidgetPage"));
const DiscoveryPage = lazy(() => import("./pages/DiscoveryPage"));
const VerifyEmailPage = lazy(() => import("./pages/VerifyEmailPage"));
const ForgotPasswordPage = lazy(() => import("./pages/ForgotPasswordPage"));
const NotFoundPage = lazy(() => import("./pages/NotFoundPage"));
const PrivacyPolicyPage = lazy(() => import("./pages/PrivacyPolicyPage"));
const TermsPage = lazy(() => import("./pages/TermsPage"));
const ClaimProfilePage = lazy(() => import("./pages/ClaimProfilePage"));
const AboutPage = lazy(() => import("./pages/AboutPage"));

import SiteBackground from "./components/SiteBackground";
import ParticlesBackground from "./components/ParticlesBackground";
import ScrollToTop from "./components/ScrollToTop";
import FloatingCopilot from "./components/FloatingCopilot";
import InstallPrompt from "./components/InstallPrompt";
import CookieConsent from "./components/CookieConsent";
import NewsletterModal from "./components/NewsletterModal";
import { ThemeProvider, useTheme } from "./contexts/ThemeContext";
import { initAttributionTracker, getAttributionSource } from "./utils/attribution";

// API backend selection:
// - REACT_APP_USE_EDGE_FUNCTIONS=true  → Supabase Edge Functions (production on Hostinger)
// - REACT_APP_USE_EDGE_FUNCTIONS=false → Python FastAPI backend (preview / local dev)
const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const USE_EDGE_FUNCTIONS = process.env.REACT_APP_USE_EDGE_FUNCTIONS === 'true';
export const API = USE_EDGE_FUNCTIONS && SUPABASE_URL ? `${SUPABASE_URL}/functions/v1/api` : `${BACKEND_URL}/api`;

// Set the Supabase apikey header for Edge Functions gateway
if (USE_EDGE_FUNCTIONS && SUPABASE_ANON_KEY) {
  axios.defaults.headers.common['apikey'] = SUPABASE_ANON_KEY;
}

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    initAttributionTracker();
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }

    // Set up global axios interceptor to handle expired or invalid tokens (401 errors)
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response && error.response.status === 401) {
          console.warn("Unauthorized request (401), logging out...");
          localStorage.removeItem('token');
          delete axios.defaults.headers.common['Authorization'];
          setUser(null);
          if (window.location.pathname !== '/auth' && window.location.pathname !== '/') {
            window.location.href = '/auth';
          }
        } else if (error.response && error.response.status >= 500) {
          toast.error("Server error. Please try again later.");
        } else if (error.request && !error.response) {
          toast.error("Network error. Please check your connection.");
        }
        return Promise.reject(error);
      }
    );

    // Safety: never stay loading for more than 8 seconds
    const timeout = setTimeout(() => setLoading(false), 8000);
    return () => {
      axios.interceptors.response.eject(interceptor);
      clearTimeout(timeout);
    };
  }, []);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      const userData = response.data;
      // If email not verified, clear session and don't auto-login
      if (userData.email_verified === false) {
        localStorage.removeItem('token');
        delete axios.defaults.headers.common['Authorization'];
        setUser(null);
        return;
      }
      setUser(userData);
    } catch (error) {
      localStorage.removeItem('token');
      delete axios.defaults.headers.common['Authorization'];
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const data = response.data;

    // If email not verified, return the flag instead of setting session
    if (data.email_not_verified) {
      return { email_not_verified: true, email: data.email };
    }

    const { token, user } = data;
    localStorage.setItem('token', token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    setUser(user);
    return user;
  };

  const register = async (formData) => {
    const source = getAttributionSource();
    const response = await axios.post(`${API}/auth/register`, { ...formData, source });
    const data = response.data;
    // Do NOT store token in localStorage - user must verify email first
    // Return token temporarily so avatar upload can work, but don't persist session
    if (data.needs_verification) {
      // Set auth header temporarily for avatar upload, but don't persist
      if (data.token) {
        axios.defaults.headers.common['Authorization'] = `Bearer ${data.token}`;
      }
      return { needs_verification: true, email: formData.email, token: data.token };
    }
    // Only auto-login if no verification needed
    const { token, user } = data;
    localStorage.setItem('token', token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    setUser(user);
    return user;
  };

  const logout = () => {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
  };

  const updateUser = (updates) => {
    setUser(prev => ({ ...prev, ...updates }));
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, updateUser, fetchUser, setUser, setToken }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children, requiredTier = null }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen" style={{ background: 'transparent' }} />
    );
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  if (requiredTier) {
    const tierLevels = { free: 0, licensed: 1, featured: 2, powerhouse: 3 };
    if (tierLevels[user.subscription_tier] < tierLevels[requiredTier]) {
      return <Navigate to="/pricing" replace />;
    }
  }

  return children;
};

// Admin-only Route Guard (requires login + admin email)
const ADMIN_EMAIL = 'admin@podcasters.directory';
const AdminRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen" style={{ background: 'transparent' }} />
    );
  }
  if (!user) return <Navigate to="/auth" replace />;
  if (user.email !== ADMIN_EMAIL) return <Navigate to="/" replace />;
  return children;
};

const PageLoader = () => (
  <div className="min-h-screen" style={{ background: 'transparent' }} />
);

// Route wrapper
const AppRoutes = () => {
  return (
    <>
      <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route path="/" element={<HomePage />} />
          <Route path="/about" element={<AboutPage />} />
        <Route path="/directory" element={<DirectoryPage />} />
        <Route path="/profile/:slug" element={<ProfilePage />} />
        <Route path="/claim-profile/:id" element={<ClaimProfilePage />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/features" element={<FeaturesPage />} />
        <Route path="/leaderboard" element={<LeaderboardPage />} />
        <Route path="/advertise" element={<AdvertisePage />} />
        <Route path="/community" element={<ProtectedRoute requiredTier="licensed"><CommunityPage /></ProtectedRoute>} />
        <Route path="/inbox" element={<ProtectedRoute requiredTier="licensed"><InboxPage /></ProtectedRoute>} />
        <Route path="/auth" element={<AuthPage />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/chat" element={<ProtectedRoute requiredTier="licensed"><ChatPage /></ProtectedRoute>} />
        <Route path="/chat/:conversationId" element={<ProtectedRoute requiredTier="licensed"><ChatPage /></ProtectedRoute>} />
        <Route path="/dashboard" element={<ProtectedRoute><UserDashboardPage /></ProtectedRoute>} />
        <Route path="/dashboard/elite" element={<ProtectedRoute requiredTier="powerhouse"><DashboardPage /></ProtectedRoute>} />
        <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
        <Route path="/subscription/success" element={<SubscriptionSuccessPage />} />
        <Route path="/blog" element={<BlogPage />} />
        <Route path="/blog/:blogSlug" element={<BlogDetailPage />} />
        <Route path="/stats" element={<Navigate to="/dashboard?tab=stats" replace />} />
        <Route path="/admin" element={<AdminRoute><AdminPage /></AdminRoute>} />
        <Route path="/marketplace" element={<Navigate to="/dashboard?tab=marketplace" replace />} />
        <Route path="/events" element={<Navigate to="/dashboard?tab=events" replace />} />
        <Route path="/copilot" element={<Navigate to="/dashboard?tab=copilot" replace />} />
        <Route path="/swap-tracker" element={<ProtectedRoute><SwapTrackerPage /></ProtectedRoute>} />
        <Route path="/comparison" element={<ProtectedRoute><ComparisonPage /></ProtectedRoute>} />
        <Route path="/share/:id" element={<ShareCardPage />} />
        <Route path="/success-stories" element={<TestimonialsPage />} />
        <Route path="/embed" element={<ProtectedRoute><EmbedWidgetPage /></ProtectedRoute>} />
        <Route path="/quiz" element={<Navigate to="/dashboard?tab=quiz" replace />} />
        <Route path="/discover" element={<DiscoveryPage />} />
        <Route path="/privacy" element={<PrivacyPolicyPage />} />
        <Route path="/privacy-policy" element={<Navigate to="/privacy" replace />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/terms-and-conditions" element={<Navigate to="/terms" replace />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
      </Suspense>
      <FloatingCopilot showOnlyOnDashboard />
      <InstallPrompt />
      <CookieConsent />
      <NewsletterModal />
    </>
  );
};

function App() {
  return (
    <ThemeProvider>
    <div className="App min-h-screen relative" style={{ backgroundColor: 'var(--pd-bg)', transition: 'background-color 0.3s ease' }}>
      <SiteBackground />
      <HelmetProvider>
      <AuthProvider>
        <BrowserRouter>
          <ParticlesBackground />
          <ScrollToTop />
          <div className="relative" style={{ zIndex: 1 }}>
            <ErrorBoundary>
              <AppRoutes />
            </ErrorBoundary>
          </div>
        </BrowserRouter>
        <Toaster position="top-right" />
      </AuthProvider>
      </HelmetProvider>
    </div>
    </ThemeProvider>
  );
}

export default App;
